import { useState, useRef, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { useVisibilityPolling } from './useVisibilityPolling';

import { fmtFixed, fmtNumber } from '../utils/format';
const BALANCE_INTERVAL = 15000;
const DATA_INTERVAL = 20000;
const SLOW_INTERVAL = 45000;

/**
 * Hook for user balance (visibility-aware polling)
 */
export const useRealtimeBalance = (userId) => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const previousBalance = useRef(null);
  const initialLoad = useRef(true);

  const fetchBalance = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get('/balance');
      const newBalance = res.data?.balance ?? 0;
      const oldBalance = previousBalance.current;
      setBalance(newBalance);
      previousBalance.current = newBalance;

      if (!initialLoad.current && oldBalance !== null && oldBalance !== newBalance) {
        const diff = newBalance - oldBalance;
        if (diff > 0) toast.success(`Balance Updated: +$${fmtFixed(diff, 2)}`);
        else if (diff < 0) toast.info(`Balance Updated: -$${fmtFixed(Math.abs(diff), 2)}`);
      }
      initialLoad.current = false;
      setLoading(false);
    } catch { setLoading(false); }
  }, [userId]);

  useVisibilityPolling(fetchBalance, BALANCE_INTERVAL, !!userId);

  return { balance, loading };
};

/**
 * Hook for user deposits (visibility-aware polling)
 */
export const useRealtimeDeposits = (userId) => {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const previousDeposits = useRef([]);

  const fetchDeposits = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get('/wallet/deposits');
      const userDeposits = res.data?.deposits || [];

      userDeposits.forEach(deposit => {
        const prev = previousDeposits.current.find(d => d.id === deposit.id);
        if (prev?.status === 'pending' && deposit.status === 'approved') {
          toast.success(`Deposit Approved: $${fmtNumber(deposit.amount, 2)}`);
        } else if (prev?.status === 'pending' && deposit.status === 'rejected') {
          toast.error('Deposit Rejected');
        }
      });

      previousDeposits.current = userDeposits;
      setDeposits(userDeposits);
      setLoading(false);
    } catch { setLoading(false); }
  }, [userId]);

  useVisibilityPolling(fetchDeposits, DATA_INTERVAL, !!userId);

  return { deposits, loading };
};

/**
 * Hook for user withdrawals (visibility-aware polling)
 */
export const useRealtimeWithdrawals = (userId) => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const previousWithdrawals = useRef([]);

  const fetchWithdrawals = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get('/wallet/withdrawals');
      const userWithdrawals = res.data?.withdrawals || [];

      userWithdrawals.forEach(w => {
        const prev = previousWithdrawals.current.find(pw => pw.id === w.id);
        if (prev?.status === 'pending' && w.status === 'approved') {
          toast.success(`Withdrawal Approved: $${fmtNumber(w.amount, 2)}`);
        } else if (prev?.status === 'pending' && w.status === 'rejected') {
          toast.error('Withdrawal Rejected');
        }
      });

      previousWithdrawals.current = userWithdrawals;
      setWithdrawals(userWithdrawals);
      setLoading(false);
    } catch { setLoading(false); }
  }, [userId]);

  useVisibilityPolling(fetchWithdrawals, DATA_INTERVAL, !!userId);

  return { withdrawals, loading };
};

/**
 * Hook for KYC status (visibility-aware polling)
 */
export const useRealtimeKYC = (userId) => {
  const [kycStatus, setKycStatus] = useState(null);
  const [kycLevel, setKycLevel] = useState(0);
  const [loading, setLoading] = useState(true);
  const previousKycStatus = useRef(null);

  const fetchKYC = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get('/auth/me');
      const userData = res.data;
      const newStatus = userData.kyc_status;
      const newLevel = userData.kyc_level || 0;

      if (previousKycStatus.current !== null && previousKycStatus.current !== newStatus) {
        if (newStatus === 'approved') toast.success(`KYC Approved - Level ${newLevel}`);
        else if (newStatus === 'rejected') toast.error('KYC Rejected');
      }

      previousKycStatus.current = newStatus;
      setKycStatus(newStatus);
      setKycLevel(newLevel);
      setLoading(false);
    } catch { setLoading(false); }
  }, [userId]);

  useVisibilityPolling(fetchKYC, SLOW_INTERVAL, !!userId);

  return { kycStatus, kycLevel, loading };
};

/**
 * Hook for user trades (visibility-aware polling)
 */
export const useRealtimeTrades = (userId) => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const previousTrades = useRef([]);

  const fetchTrades = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get('/trade/active-orders');
      const userTrades = res.data || [];

      userTrades.forEach(trade => {
        const prev = previousTrades.current.find(t => t.trade_id === trade.trade_id);
        // Binary trades terminate with status='won'/'lost'; arbitrage/legacy use status='completed' + outcome
        const wasActive = prev?.status === 'active';
        const isWon = trade.status === 'won' || (trade.status === 'completed' && trade.outcome === 'win');
        const isLost = trade.status === 'lost' || (trade.status === 'completed' && trade.outcome === 'loss');
        if (wasActive && isWon) {
          toast.success(`Trade Won: +$${(trade.profit != null ? fmtFixed(trade.profit, 2) : undefined) || (trade.actual_profit != null ? fmtFixed(trade.actual_profit, 2) : undefined) || '0.00'}`);
        } else if (wasActive && isLost) {
          toast.error(`Trade Lost: -$${fmtNumber(trade.amount, 2)}`);
        }
      });

      previousTrades.current = userTrades;
      setTrades(userTrades);
      setLoading(false);
    } catch { setLoading(false); }
  }, [userId]);

  useVisibilityPolling(fetchTrades, DATA_INTERVAL, !!userId);

  return { trades, loading };
};

/**
 * Hook for user notifications (visibility-aware polling)
 */
// Session-wide so per-route provider remounts never re-toast old notifications
const seenNotificationIds = new Set();
let notificationsPrimed = false;

export const useRealtimeNotifications = (userId) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get('/notifications');
      const userNotifications = (res.data?.notifications || [])
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      userNotifications.forEach(n => {
        const isNew = !seenNotificationIds.has(n.id);
        seenNotificationIds.add(n.id);
        if (notificationsPrimed && isNew && !n.read) toast.info(n.title, { description: n.message });
      });
      notificationsPrimed = true;
      setNotifications(userNotifications);
      setUnreadCount(userNotifications.filter(n => !n.read).length);
    } catch { /* silent */ }
  }, [userId]);

  useVisibilityPolling(fetchNotifications, DATA_INTERVAL, !!userId);

  return { notifications, unreadCount };
};

/**
 * Hook for deposit addresses (visibility-aware polling)
 */
export const useRealtimeDepositAddresses = (userId) => {
  const [addresses, setAddresses] = useState(null);
  const [isUserSpecific, setIsUserSpecific] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAddresses = useCallback(async () => {
    try {
      const res = await api.get('/deposit-addresses/all');
      const data = res.data;

      // Merge: start with platform default, then overlay any user-specific overrides per (crypto, network)
      const userAddrs = data?.user_addresses || {};
      const platformAddrs = data?.platform_addresses || {};
      const merged = JSON.parse(JSON.stringify(platformAddrs));
      let overrideFound = false;
      for (const crypto of Object.keys(userAddrs)) {
        if (typeof userAddrs[crypto] !== 'object' || !userAddrs[crypto]) continue;
        if (!merged[crypto]) merged[crypto] = {};
        for (const network of Object.keys(userAddrs[crypto])) {
          const addr = userAddrs[crypto][network];
          if (addr && String(addr).trim()) {
            merged[crypto][network] = addr;
            overrideFound = true;
          }
        }
      }

      setAddresses(merged);
      setIsUserSpecific(overrideFound);
      setLoading(false);
    } catch {
      setAddresses({});
      setLoading(false);
    }
  }, []);

  useVisibilityPolling(fetchAddresses, 60000, true);

  return { addresses, isUserSpecific, loading };
};
